import { Bookmark, ListPlus } from '@phosphor-icons/react'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import {
  AddToPlaylistButton,
  BookmarkButton,
  LikeButton
} from '../../../components/action-buttons'
import { useAddToBookmarks } from '../../../components/add-to-bookmarks'
import { BaseContentCard } from '../../../components/general-card'
import { useCardExpand } from '../../../components/general-card/hooks'
import { useLike } from '../../../components/like'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import { useHomeEnterpriseContext } from '../context/home-enterprise-page-context'
import { useTrackHomeEnterpriseEvent } from '../hooks/useTrackHomeEnterpriseEvent'

export const HomeContentCard = ({
  content,
  dataTest,
  railType,
  itemIndex,
  extraTrackingData = {}
}) => {
  const navigate = useNavigate()
  const { showAddToPlaylist } = useHomeEnterpriseContext()
  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({
    from: 'home-enterprise'
  })
  const { isLiked, handleLike, liking } = useLike({ from: 'home-enterprise' })
  const { trackArticleClickEvent } = useTrackHomeEnterpriseEvent()

  const onSelect = useCallback(() => {
    if (!content) return
    trackArticleClickEvent(railType, content.locationId, {
      title: content.title,
      itemIndex,
      ...extraTrackingData
    })
    navigate(`/${content.locationId}/${content.staticUrl}?from=home-enterprise`)
  }, [
    railType,
    navigate,
    content,
    trackArticleClickEvent,
    itemIndex,
    extraTrackingData
  ])

  const onBookmark = useCallback(() => {
    if (!content) return
    handleBookmark(content)
  }, [handleBookmark, content])

  return (
    <ContentCard
      content={content}
      onSelect={onSelect}
      onBookmark={onBookmark}
      bookmarked={isBookmarked(content)}
      isBookmarking={bookmarking(content)}
      isLiking={liking(content || {})}
      onLike={() => handleLike(content)}
      isLiked={isLiked(content || {})}
      onAddToPlaylist={() => showAddToPlaylist(content)}
      testId={dataTest}
    />
  )
}

const useStyles = makeStyles(({ spacing }) => ({
  subtitle: {
    lineClamp: 2,
    WebkitLineClamp: 2
  },
  title: {
    minHeight: 'auto'
  },
  root: {
    height: spacing(45)
  },
  card: {
    height: '100%'
  }
}))

/** Card for displaying content such as articles, videos and audio etc */
const ContentCard = ({
  onAddToPlaylist,
  onBookmark,
  bookmarked,
  isBookmarking,
  isLiking,
  onLike,
  isLiked,
  ...props
}) => {
  const displayAddToPlaylist = useDisplayAddToPlaylist()
  const classes = useStyles()

  const { hoverShadow, handleMouseEnter, handleMouseLeave } = useCardExpand()

  const actions = {
    leftActions: (
      <>
        <Box
          display='flex'
          flexDirection='row'
          gap={1}
          justifyContent='flex-start'
          alignItems='center'
        >
          <LikeButton
            size='xs'
            label=''
            hasLiked={isLiked}
            loading={isLiking}
            hideText
            onClick={onLike}
            aria-busy={isLiking}
            aria-label={isLiked ? 'Unlike content' : 'Like content'}
          />
          <BookmarkButton
            size='xs'
            variant={bookmarked ? 'secondary' : 'secondary-noselect'}
            onClick={onBookmark}
            loading={isBookmarking}
            hideText
            bookmarked={bookmarked}
            startIcon={<Bookmark size={18} weight={bookmarked ? 'fill' : 'regular'} />}
            aria-busy={isBookmarking}
            aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
          />
          {displayAddToPlaylist && (
            <AddToPlaylistButton
              size='xs'
              variant='secondary-noselect'
              startIcon={<ListPlus size={18} weight='duotone' />}
              onClick={() => {
                onAddToPlaylist(props.content)
              }}
              aria-label='Add this item to a playlist'
            />
          )}
        </Box>
      </>
    )
  }
  return (
    <BaseContentCard
      actions={actions}
      expand
      hoverShadow={hoverShadow}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      overrides={{
        title: classes.title,
        subtitle: classes.subtitle,
        root: classes.root,
        card: classes.card
      }}
      {...props}
    />
  )
}

ContentCard.defaultProps = {
  size: 'small'
}
export default ContentCard
